import { useState } from "react";
import { FiBookmark } from "react-icons/fi";
import { Button, showToast } from "../../../../components/shared";
import { useSaveKnowledgeItem } from "../../../../lib/react-query/knowledge";
import { extractBackendErrorMessage } from "../../../../utils/utils";

const cleanText = (value = "") => value.replace(/\s+/g, " ").trim();

const inferTitle = (content = "") => {
  const text = cleanText(content);
  if (!text) return "Saved chat insight";
  const sentence = text.match(/^(.+?[.!?])\s/)?.[1] || text;
  return sentence.length > 80 ? `${sentence.slice(0, 77).trim()}...` : sentence;
};

const looksLikeDecision = (content = "") =>
  /\b(decided|approved|selected|rejected|agreed|confirmed|will proceed|next step|action item)\b/i.test(content);

export const SaveActions = ({ teamId, projectId, message }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [actionTaken, setActionTaken] = useState("");
  const [showAction, setShowAction] = useState(false);
  const { mutateAsync: saveItem, isPending } = useSaveKnowledgeItem(teamId);
  if (!teamId || !message?.id) return null;

  const openForm = () => {
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    if (nextOpen && !title && !body) {
      setTitle(inferTitle(message.content));
      setBody(message.content || "");
      setShowAction(looksLikeDecision(message.content));
    }
  };

  const submit = async () => {
    if (!title.trim()) return;
    const isDecision = Boolean(actionTaken.trim());
    const details = body.trim() || message.content;
    const savedBody = isDecision
      ? `Decision:\n${details}\n\nAction taken:\n${actionTaken.trim()}`
      : details;
    try {
      await saveItem({
        kind: isDecision ? "SAVED_DECISION" : "NOTE",
        memory_type: projectId ? "PROJECT" : "COMPANY",
        project: projectId || null,
        title: title.trim(),
        body: savedBody,
        source_message_id: message.id,
      });
      showToast("Saved to knowledge", "success", { position: "top-center" });
      setIsOpen(false);
      setTitle("");
      setBody("");
      setActionTaken("");
      setShowAction(false);
    } catch (err) {
      showToast(extractBackendErrorMessage(err, "Failed to save"), "error", {
        position: "top-center",
      });
    }
  };

  return (
    <div className="mt-1.5">
      <div className="group relative inline-block">
        <button
          type="button"
          onClick={openForm}
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] text-mediumgrey opacity-0 group-hover:opacity-100 hover:bg-surface transition-opacity"
          aria-label="Save to knowledge"
        >
          <FiBookmark aria-hidden="true" size={11} />
          Save
        </button>
      </div>
      {isOpen ? (
        <div className="rounded-lg border border-surface-border bg-white p-2">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title"
            className="mb-2 w-full rounded-md border border-surface-border px-2 py-1 text-xs outline-none focus:border-primary"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Details"
            rows={2}
            className="w-full resize-none rounded-md border border-surface-border px-2 py-1 text-xs outline-none focus:border-primary"
          />
          {showAction ? (
            <textarea
              value={actionTaken}
              onChange={(e) => setActionTaken(e.target.value)}
              placeholder="Action taken"
              rows={2}
              className="mt-2 w-full resize-none rounded-md border border-surface-border px-2 py-1 text-xs outline-none focus:border-primary"
            />
          ) : (
            <button
              type="button"
              onClick={() => setShowAction(true)}
              className="mt-2 text-[11px] text-primary hover:underline"
            >
              Add action taken
            </button>
          )}
          <div className="mt-2 flex justify-end gap-2">
            <Button className="h-7 rounded-md bg-surface px-2 text-xs text-mediumgrey" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button className="h-7 rounded-md bg-primary px-2 text-xs text-white" onClick={submit} disabled={!title.trim() || isPending}>
              Save
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
